/**
 * Gizmo Snap Service
 *
 * Enables absolute grid snapping when Ctrl or Alt (Option on Mac) is held during gizmo drags:
 * - Position gizmo: snaps to 0.1 unit grid (absolute alignment)
 * - Rotation gizmo: snaps to 15° increments (absolute alignment)
 * - Scale gizmo: snaps to 0.1 increments (absolute alignment)
 *
 * Scale boost: Holding Shift (without Ctrl/Alt) during a scale gizmo drag multiplies
 * the scale delta by 5× for faster scaling.
 *
 * Approach: We maintain a "true" unsnapped position accumulator alongside the displayed
 * snapped position. On drag start we capture the node's current position. On each drag
 * tick Babylon applies a free-form delta to the node; we read back the result as the
 * new "true" position, then overwrite the node with the rounded grid value. This way
 * small movements accumulate naturally until they cross a grid threshold.
 *
 * Rotation uses the sub-gizmo's accumulated .angle property (a clean scalar) combined
 * with the starting Euler angle on the dragged axis. This avoids quaternion↔Euler
 * round-trip instability near gimbal lock (±90° X).
 *
 * Uses the same prototype-patching pattern as undo-gizmo-service.ts to track
 * live gizmo instances.
 */

import type { ServiceDefinition, IGizmoService } from '@babylonjs/inspector';
import { GizmoServiceIdentity } from '@babylonjs/inspector';
import { PositionGizmo } from '@babylonjs/core/Gizmos/positionGizmo';
import { RotationGizmo } from '@babylonjs/core/Gizmos/rotationGizmo';
import type { IPlaneRotationGizmo } from '@babylonjs/core/Gizmos/planeRotationGizmo';
import { ScaleGizmo } from '@babylonjs/core/Gizmos/scaleGizmo';
import { Quaternion, Vector3 } from '@babylonjs/core/Maths/math.vector';
import { Epsilon } from '@babylonjs/core/Maths/math.constants';
import { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import type { Observer } from '@babylonjs/core/Misc/observable';
import type { DragEvent, DragStartEndEvent } from '@babylonjs/core/Behaviors/Meshes/pointerDragEvents';
import type { Scene } from '@babylonjs/core/scene';

// ============================================================================
// Snap constants
// ============================================================================

// Fine snap (Ctrl/Alt)
const POSITION_SNAP = 0.1;                // 0.1 unit grid
const ROTATION_SNAP = Math.PI / 12;       // 15 degrees
const SCALE_SNAP = 0.1;

// Coarse snap (Ctrl/Alt + Shift)
const POSITION_SNAP_COARSE = 0.5;         // 0.5 unit grid
const ROTATION_SNAP_COARSE = Math.PI / 4; // 45 degrees
const SCALE_SNAP_COARSE = 0.5;

// Scale boost (Shift without Ctrl/Alt)
export const SCALE_BOOST = 5;

// ============================================================================
// Helpers
// ============================================================================

function roundTo(value: number, step: number): number {
    return Math.round(value / step) * step;
}

// ============================================================================
// Observers & drag state
// ============================================================================

type GizmoInstance = PositionGizmo | RotationGizmo | ScaleGizmo;

interface GizmoObserverSet {
    drag: Observer<DragEvent>;
    dragStart: Observer<DragStartEndEvent>;
    dragEnd: Observer<DragStartEndEvent>;
}

const observerMap = new Map<object, GizmoObserverSet>();

/**
 * Per-gizmo drag state. We maintain two values:
 * - `true`: the unsnapped accumulator — what the value would be without rounding
 * - `snapped`: the last rounded value we wrote to the node
 *
 * On each drag tick Babylon adds a delta to the node. But the node holds the
 * snapped (rounded) value, so: node_current = snapped + delta.
 * We recover delta = node_current - snapped, then: true += delta, node = round(true).
 */
interface VectorDragState {
    kind: 'vector';
    true: [number, number, number];
    snapped: [number, number, number];
}

/**
 * Per-gizmo drag state for rotation. Instead of converting between quaternion and
 * Euler angles each tick (which breaks near gimbal lock at ±90° X), we use the
 * sub-gizmo's accumulated .angle property as a clean scalar delta from drag start.
 *
 * We capture the starting Euler angle on the dragged axis, then on each tick compute:
 *   targetAngle = startAngle + subGizmo.angle
 *   snappedAngle = roundTo(targetAngle, step)
 * and apply the rotation as: startQuaternion * RotationAxis(axis, snappedAngle - startAngle)
 *
 * This keeps all intermediate math in either scalar or quaternion space — no Euler
 * round-trips during the drag.
 */
interface RotationDragState {
    kind: 'rotation';
    /** The node's quaternion at drag start */
    startQuat: Quaternion;
    /** The Euler angle on the dragged axis at drag start */
    startAngle: number;
    /** Which axis is being dragged: 0=x, 1=y, 2=z */
    axisIndex: number;
    /** Reference to the sub-gizmo being dragged (has .angle accumulator) */
    subGizmo: IPlaneRotationGizmo;
}

type DragState = VectorDragState | RotationDragState;

const dragState = new Map<object, DragState>();

// ============================================================================
// Rotation proxy — workaround for non-uniform scale
// ============================================================================
//
// Babylon's PlaneRotationGizmo refuses to rotate when the attached node has
// non-uniform scale and updateGizmoRotationToMatchAttachedMesh is true. It
// decomposes the world matrix to TRS, rotates the quaternion, then recomposes
// — but that round-trip is lossy with non-uniform scale + rotation.
//
// We fix this by attaching the gizmo to an unparented proxy TransformNode with
// scale [1,1,1]. The proxy's TRS decomposition is always lossless. On each
// drag tick we sync the proxy's rotation back to the real node.

interface RotationProxy {
    proxy: TransformNode;
    realNode: TransformNode;
    beforeRenderObserver: Observer<Scene>;
    dragging: boolean;
}

const proxyMap = new Map<RotationGizmo, RotationProxy>();

// Reusable temporaries to avoid per-frame allocation
const _tmpScale = new Vector3();
const _tmpRotation = new Quaternion();
const _tmpPosition = new Vector3();

function hasNonUniformScale(node: TransformNode): boolean {
    node.getWorldMatrix().decompose(_tmpScale);
    return Math.abs(Math.abs(_tmpScale.x) - Math.abs(_tmpScale.y)) > Epsilon
        || Math.abs(Math.abs(_tmpScale.x) - Math.abs(_tmpScale.z)) > Epsilon;
}

/** Copy world position and rotation from real node to proxy (no scale). */
function syncRealToProxy(realNode: TransformNode, proxy: TransformNode): void {
    realNode.getWorldMatrix().decompose(undefined, _tmpRotation, _tmpPosition);
    proxy.position.copyFrom(_tmpPosition);
    proxy.rotationQuaternion!.copyFrom(_tmpRotation);
    proxy.computeWorldMatrix(true);
}

/** Copy rotation from proxy back to real node. */
function syncProxyRotationToReal(proxy: TransformNode, realNode: TransformNode): void {
    if (!proxy.rotationQuaternion) return;
    if (!realNode.rotationQuaternion) {
        realNode.rotationQuaternion = proxy.rotationQuaternion.clone();
    } else {
        realNode.rotationQuaternion.copyFrom(proxy.rotationQuaternion);
    }
    realNode.computeWorldMatrix(true);
    realNode.updateCache();
}

function createRotationProxy(realNode: TransformNode, gizmo: RotationGizmo): RotationProxy {
    const scene = realNode.getScene();
    const proxy = new TransformNode('__rotation_gizmo_proxy__', scene);
    proxy.rotationQuaternion = new Quaternion(0, 0, 0, 1);

    syncRealToProxy(realNode, proxy);

    // While not dragging, keep proxy tracking the real node each frame
    const beforeRenderObserver = scene.onBeforeRenderObservable.add(() => {
        const entry = proxyMap.get(gizmo);
        if (!entry || entry.dragging) return;
        syncRealToProxy(entry.realNode, entry.proxy);
    });

    return { proxy, realNode, beforeRenderObserver, dragging: false };
}

function disposeRotationProxy(gizmo: RotationGizmo): void {
    const entry = proxyMap.get(gizmo);
    if (!entry) return;
    entry.proxy.getScene().onBeforeRenderObservable.remove(entry.beforeRenderObserver);
    entry.proxy.dispose();
    proxyMap.delete(gizmo);
}

let snapping = false;
let coarseSnap = false;
let shiftHeld = false;

/** Whether scale boost is active (Shift held without Ctrl/Alt). */
export function isScaleBoosting(): boolean {
    return !snapping && shiftHeld;
}

// ============================================================================
// Drag lifecycle callbacks
// ============================================================================

function readVectorValue(gizmo: PositionGizmo | ScaleGizmo): [number, number, number] | null {
    const node = gizmo.attachedNode;
    if (!node || !(node instanceof TransformNode)) return null;

    if (gizmo instanceof PositionGizmo) {
        return [node.position.x, node.position.y, node.position.z];
    } else {
        return [node.scaling.x, node.scaling.y, node.scaling.z];
    }
}

function writeSnappedVector(gizmo: PositionGizmo | ScaleGizmo, v: [number, number, number], step: number): [number, number, number] {
    const node = gizmo.attachedNode as TransformNode;
    const s: [number, number, number] = [roundTo(v[0], step), roundTo(v[1], step), roundTo(v[2], step)];

    if (gizmo instanceof PositionGizmo) {
        node.position.x = s[0];
        node.position.y = s[1];
        node.position.z = s[2];
    } else {
        node.scaling.x = s[0];
        node.scaling.y = s[1];
        node.scaling.z = s[2];
    }

    node.computeWorldMatrix(true);
    node.updateCache();
    return s;
}

/** Find which sub-gizmo of a RotationGizmo is currently being dragged. */
function findActiveSubGizmo(gizmo: RotationGizmo): [number, IPlaneRotationGizmo] | null {
    if (gizmo.xGizmo.dragBehavior?.dragging) return [0, gizmo.xGizmo];
    if (gizmo.yGizmo.dragBehavior?.dragging) return [1, gizmo.yGizmo];
    if (gizmo.zGizmo.dragBehavior?.dragging) return [2, gizmo.zGizmo];
    return null;
}

/** Axis unit vectors for X, Y, Z rotation */
const axisVectors = [
    new Vector3(1, 0, 0),  // X axis
    new Vector3(0, 1, 0),  // Y axis
    new Vector3(0, 0, 1),  // Z axis
];

function snapStep(gizmo: GizmoInstance): number {
    if (gizmo instanceof PositionGizmo) return coarseSnap ? POSITION_SNAP_COARSE : POSITION_SNAP;
    if (gizmo instanceof RotationGizmo) return coarseSnap ? ROTATION_SNAP_COARSE : ROTATION_SNAP;
    return coarseSnap ? SCALE_SNAP_COARSE : SCALE_SNAP;
}

function onDragStart(gizmo: GizmoInstance): void {
    if (gizmo instanceof RotationGizmo) {
        // If proxy is active, do a final real→proxy sync and pause beforeRender sync
        const proxyEntry = proxyMap.get(gizmo);
        if (proxyEntry) {
            syncRealToProxy(proxyEntry.realNode, proxyEntry.proxy);
            proxyEntry.dragging = true;
        }

        const node = gizmo.attachedNode; // proxy if active, real node otherwise
        if (!node || !(node instanceof TransformNode)) return;

        const active = findActiveSubGizmo(gizmo);
        if (!active) return;
        const [axisIndex, subGizmo] = active;

        // Capture starting quaternion
        const startQuat = node.rotationQuaternion
            ? node.rotationQuaternion.clone()
            : Quaternion.FromEulerAngles(node.rotation.x, node.rotation.y, node.rotation.z);

        // Get starting Euler angle on the dragged axis
        const euler = startQuat.toEulerAngles();
        const angles = [euler.x, euler.y, euler.z];

        dragState.set(gizmo, {
            kind: 'rotation',
            startQuat,
            startAngle: angles[axisIndex],
            axisIndex,
            subGizmo,
        });
    } else {
        // Always capture state for scale gizmos (needed for Shift boost even without snap),
        // and for position gizmos when snapping is active.
        const v = readVectorValue(gizmo);
        if (!v) return;
        dragState.set(gizmo, { kind: 'vector', true: [...v], snapped: [...v] });
    }
}

function onDrag(gizmo: GizmoInstance): void {
    const node = gizmo.attachedNode;
    if (!node || !(node instanceof TransformNode)) return;

    const state = dragState.get(gizmo);
    if (!state) return;

    // Scale boost: Shift without Ctrl/Alt amplifies scale delta by 3×
    const scaleBoosting = !snapping && shiftHeld && gizmo instanceof ScaleGizmo;

    const hasProxy = gizmo instanceof RotationGizmo && proxyMap.has(gizmo);

    // For rotation with proxy we must always run (to sync proxy→real).
    if (!snapping && !scaleBoosting && !hasProxy) return;

    if (snapping && gizmo instanceof RotationGizmo && state.kind === 'rotation') {
        const step = snapStep(gizmo);

        // The sub-gizmo's .angle is the accumulated rotation in radians since dragStart
        const angleDelta = state.subGizmo.angle;

        // Absolute target = start + accumulated delta
        const targetAngle = state.startAngle + angleDelta;
        const snappedAngle = roundTo(targetAngle, step);

        // Build snapped rotation: startQuat * RotationAxis(axis, snappedDelta)
        const snappedDelta = snappedAngle - state.startAngle;
        const deltaQuat = Quaternion.RotationAxis(axisVectors[state.axisIndex], snappedDelta);
        const result = state.startQuat.multiply(deltaQuat);

        if (node.rotationQuaternion) {
            node.rotationQuaternion.copyFrom(result);
        } else {
            const e = result.toEulerAngles();
            node.rotation.x = e.x;
            node.rotation.y = e.y;
            node.rotation.z = e.z;
        }

        node.computeWorldMatrix(true);
        node.updateCache();
    }

    // Sync proxy rotation to real node after snap logic (or after Babylon's
    // built-in drag handler when snap is not active). Must happen every tick
    // so the undo service sees correct state on drag end.
    if (hasProxy) {
        const entry = proxyMap.get(gizmo)!;
        syncProxyRotationToReal(entry.proxy, entry.realNode);
        return;
    }

    if (state.kind === 'vector' && (gizmo instanceof PositionGizmo || gizmo instanceof ScaleGizmo)) {
        const current = readVectorValue(gizmo);
        if (!current) return;

        // Recover the delta Babylon applied this tick
        let dx = current[0] - state.snapped[0];
        let dy = current[1] - state.snapped[1];
        let dz = current[2] - state.snapped[2];

        // Amplify delta when scale boosting
        if (scaleBoosting) {
            dx *= SCALE_BOOST;
            dy *= SCALE_BOOST;
            dz *= SCALE_BOOST;
        }

        // Accumulate into true unsnapped position
        state.true[0] += dx;
        state.true[1] += dy;
        state.true[2] += dz;

        if (snapping) {
            // Write rounded value to node, store as new snapped base
            const step = snapStep(gizmo);
            state.snapped = writeSnappedVector(gizmo, state.true, step);
        } else {
            // No snap — write the boosted value directly
            const n = gizmo.attachedNode as TransformNode;
            n.scaling.x = state.true[0];
            n.scaling.y = state.true[1];
            n.scaling.z = state.true[2];
            n.computeWorldMatrix(true);
            n.updateCache();
            state.snapped = [...state.true];
        }
    }
}

function onDragEnd(gizmo: GizmoInstance): void {
    dragState.delete(gizmo);

    // Final proxy→real sync and resume beforeRender real→proxy sync
    if (gizmo instanceof RotationGizmo) {
        const entry = proxyMap.get(gizmo);
        if (entry) {
            syncProxyRotationToReal(entry.proxy, entry.realNode);
            entry.dragging = false;
        }
    }
}

// ============================================================================
// Hook gizmo observables
// ============================================================================

function hookGizmo(gizmo: GizmoInstance): void {
    if (observerMap.has(gizmo)) return;

    const dragStart = gizmo.onDragStartObservable?.add(() => onDragStart(gizmo));
    const drag = gizmo.onDragObservable?.add(() => onDrag(gizmo));
    const dragEnd = gizmo.onDragEndObservable?.add(() => onDragEnd(gizmo));

    if (dragStart && drag && dragEnd) {
        observerMap.set(gizmo, { dragStart, drag, dragEnd });
    }
}

// ============================================================================
// Prototype patching — hook gizmo instances as they attach to nodes
// ============================================================================

interface PatchState {
    originalDescriptor: PropertyDescriptor;
    prototype: object;
    property: string;
}

const patches: PatchState[] = [];

function patchGizmoClass(
    GizmoClass: abstract new (...args: never[]) => unknown,
    property: 'attachedNode' | 'attachedMesh' = 'attachedNode'
): void {
    const proto = GizmoClass.prototype as object;
    const descriptor = Object.getOwnPropertyDescriptor(proto, property);
    if (!descriptor?.set) return;

    const origDesc = descriptor;
    patches.push({ originalDescriptor: descriptor, prototype: proto, property });

    Object.defineProperty(proto, property, {
        get: origDesc.get ? function(this: object) { return origDesc.get!.call(this) as unknown; } : undefined,
        set: function (this: GizmoInstance, value: unknown) {
            if (this instanceof RotationGizmo) {
                disposeRotationProxy(this);

                if (value instanceof TransformNode && hasNonUniformScale(value)) {
                    const entry = createRotationProxy(value, this);
                    proxyMap.set(this, entry);
                    origDesc.set!.call(this, entry.proxy);
                    hookGizmo(this);
                    return;
                }
            }

            origDesc.set!.call(this, value);
            if (value) {
                hookGizmo(this);
            }
        },
        configurable: true,
        enumerable: true,
    });
}

function unpatchAll(): void {
    // Dispose all rotation proxies
    for (const gizmo of proxyMap.keys()) {
        disposeRotationProxy(gizmo);
    }

    for (const { originalDescriptor, prototype, property } of patches) {
        Object.defineProperty(prototype, property, originalDescriptor);
    }
    patches.length = 0;
    for (const obs of observerMap.values()) {
        obs.dragStart.remove();
        obs.drag.remove();
        obs.dragEnd.remove();
    }
    observerMap.clear();
    dragState.clear();
}

// ============================================================================
// Service Definition
// ============================================================================

/** Module-level ref so other code can read the Inspector's gizmo service. */
let gizmoServiceRef: IGizmoService | null = null;

/** Get the Inspector's gizmo service (available after the inspector opens). */
export function getGizmoService(): IGizmoService | null {
    return gizmoServiceRef;
}

export const GizmoSnapServiceDefinition: ServiceDefinition<[], [IGizmoService]> = {
    friendlyName: 'Gizmo Snap Service',

    consumes: [GizmoServiceIdentity],

    factory: (_gizmoService: IGizmoService) => {
        gizmoServiceRef = _gizmoService;
        // Patch gizmo classes to track instances
        patchGizmoClass(PositionGizmo, 'attachedNode');
        patchGizmoClass(PositionGizmo, 'attachedMesh');
        patchGizmoClass(RotationGizmo, 'attachedNode');
        patchGizmoClass(RotationGizmo, 'attachedMesh');
        patchGizmoClass(ScaleGizmo, 'attachedNode');
        patchGizmoClass(ScaleGizmo, 'attachedMesh');

        const onKeyDown = (e: KeyboardEvent) => {
            shiftHeld = e.shiftKey;
            if (e.ctrlKey || e.altKey) {
                snapping = true;
                coarseSnap = e.shiftKey;
            }
        };

        const onKeyUp = (e: KeyboardEvent) => {
            shiftHeld = e.shiftKey;
            if (!(e.ctrlKey || e.altKey)) {
                snapping = false;
                coarseSnap = false;
            } else {
                coarseSnap = e.shiftKey;
            }
        };

        const onBlur = () => {
            snapping = false;
            coarseSnap = false;
            shiftHeld = false;
        };

        window.addEventListener('keydown', onKeyDown);
        window.addEventListener('keyup', onKeyUp);
        window.addEventListener('blur', onBlur);

        return {
            dispose: () => {
                window.removeEventListener('keydown', onKeyDown);
                window.removeEventListener('keyup', onKeyUp);
                window.removeEventListener('blur', onBlur);
                snapping = false;
                coarseSnap = false;
                shiftHeld = false;
                gizmoServiceRef = null;
                unpatchAll();
            },
        };
    },
};
